import config from "@/config";
import {
  ARTWORK_CONTINUATION_POLICIES,
  SUARTEWORLD_SEGMENT_ARTWORK_LIMIT,
} from "@/shared/constants/homeCarousel";

export const SUARTEWORLD_SEGMENT_SOURCE = {
  type: "suarteworld",
  label: "Suarteworld",
};

export function hasSuarteworldContinuationPolicy(source) {
  return source?.continuation === ARTWORK_CONTINUATION_POLICIES.SUARTEWORLD;
}

export function shouldAppendSuarteworldSegment({
  feedSource,
  artworksLength,
  totalDocs,
  activeSlide,
  segmentStatus,
}) {
  const exhausted = artworksLength >= totalDocs;
  const closeToEnd = activeSlide >= Math.max(artworksLength - 2, 0);

  return (
    hasSuarteworldContinuationPolicy(feedSource)
    && exhausted
    && closeToEnd
    && segmentStatus === "idle"
  );
}

export async function appendSuarteworldSegment({
  fetchWrapper,
  expectedFeedEndpoint,
  currentArtworks,
  setArtworks,
  applySourceToArtworkList,
  mergeUniqueArtworks,
  cacheHandler,
  listCacheKey,
  locationCacheKey,
  logRouting,
}) {
  const suarteworldEndpoint = `${config.apis.api.url}/home/links/randomized/artworks?artwork_limit=${SUARTEWORLD_SEGMENT_ARTWORK_LIMIT}`;

  logRouting?.("suarteworld-segment-request", {
    endpoint: suarteworldEndpoint,
    feedEndpoint: expectedFeedEndpoint,
    feedSourceType: currentArtworks?.data?.[0]?._artworkViewSource?.type ?? null,
    feedDataLength: currentArtworks?.data?.length ?? 0,
    feedTotalDocs: currentArtworks?.totalDocs ?? 0,
  });

  const { response, data } = await fetchWrapper.get(suarteworldEndpoint, {
    optionalToken: true,
  });

  if (!response.ok || !Array.isArray(data) || data.length === 0) {
    logRouting?.("suarteworld-segment-empty-or-failed", {
      status: response.status,
      hasArrayData: Array.isArray(data),
      count: Array.isArray(data) ? data.length : 0,
    });
    return false;
  }

  const normalizedIncoming = applySourceToArtworkList(data, SUARTEWORLD_SEGMENT_SOURCE, true);

  let dataToStore = null;
  let addedCount = 0;
  let nextTotalDocs = currentArtworks?.totalDocs ?? 0;

  setArtworks((prev) => {
    if (!prev || prev.endpoint !== expectedFeedEndpoint) {
      logRouting?.("suarteworld-segment-stale-response-ignored", {
        requestFeedEndpoint: expectedFeedEndpoint,
        currentFeedEndpoint: prev?.endpoint ?? null,
      });
      return prev;
    }

    const mergedData = mergeUniqueArtworks(prev.data, normalizedIncoming);
    addedCount = Math.max(mergedData.length - prev.data.length, 0);
    nextTotalDocs = prev.totalDocs + addedCount;
    dataToStore = {
      ...prev,
      data: mergedData,
      totalDocs: nextTotalDocs,
    };
    return dataToStore;
  });

  if (dataToStore) {
    const keyToStore = listCacheKey || locationCacheKey;
    if (keyToStore) {
      cacheHandler.storeInCache(keyToStore, dataToStore, {
        invalidateWhen: ["EDIT_ARTWORK", "DELETE_ARTWORK", "BUY_ARTWORK"],
      });
    }
  }

  logRouting?.("suarteworld-segment-appended", {
    appendedCount: addedCount,
    resultingLength: dataToStore?.data?.length ?? currentArtworks?.data?.length ?? 0,
    resultingTotalDocs: nextTotalDocs,
  });

  return addedCount > 0;
}
